import React, { useMemo } from "react";
import { NavLink } from "react-router-dom";

import type { NavItemConfig } from "../mainNavConfig";

export type SubNavbarProps = {
	/** The ID of the currently active tab (used to highlight the active link) */
	activeTabId: string | null;
	/** The main tab group (e.g., "engagement", "performance") for styling purposes */
	activeMainTab: string | null;
	/** The sub-navigation items to display */
	subNavItems: NavItemConfig[];
	/** Optional aria-label for the nav element */
	ariaLabel?: string;
};

/**
 * SubNavbar - Exact replica of the legacy secondary nav
 * All styling handled by SubNavbar.scss (copied from nav.css)
 */
export const SubNavbar = ({ activeTabId, activeMainTab, subNavItems, ariaLabel }: SubNavbarProps) => {
	if (!subNavItems || subNavItems.length === 0) {
		return null;
	}

	const [containerClasses, linkActiveClasses] = useMemo(() => {
		return activeMainTab === "engagement"
			? ["bg-primary-9", "text-neutral-3 data-[active=true]:text-neutral-0 before:bg-neutral-0"]
			: ["bg-neutral-2", "text-neutral-7 data-[active=true]:text-neutral-9 before:bg-neutral-9"];
	}, [activeMainTab]);

	return (
		<nav
			aria-label={ariaLabel || "Secondary navigation"}
			className={`flex h-10 w-full items-center justify-center text-paragraph-3 font-semibold ${containerClasses}`}
		>
			<ul className="flex h-full items-center justify-center gap-10">
				{subNavItems.map((item) => {
					const isActive = activeTabId === item.id;

					return (
						<li key={item.id} data-nav-item={item.id} className="flex h-full items-center justify-center">
							<NavLink
								to={item.href}
								className={`relative flex h-full items-center justify-center before:absolute before:bottom-0 before:left-0 before:h-0.5 before:w-0 before:origin-left before:transition-all before:duration-300 before:ease-out before:content-[''] hover:before:w-full data-[active=true]:before:w-full ${linkActiveClasses} `}
								aria-current={isActive ? "page" : undefined}
								data-active={isActive}
							>
								{item.title}
							</NavLink>
						</li>
					);
				})}
			</ul>
		</nav>
	);
};
